import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import { useAuth } from "../../../hooks/useAuth";
import EditScores from "./editScores/EditScores";
import LiveRoundControl from "./liveScorecard/LiveRoundControl";
import TournamentStandings from "./tournamentMainPage/components/TournamentStandings";
import MiniGamesStandings from "./miniGames/MinigamesStandings";
import TournamentPlayers from "./players/Players";
import NewTournament from "../../NewTournament";
import { getTournamentTheme } from "../../../styles/themes";

export default function Tournament() {
  const { user, loading: authLoading } = useAuth();
  const { id } = useParams();

  const [isAdmin, setIsAdmin] = useState(false);
  const [tournament, setTournament] =
    useState(null);
  const [activeTab, setActiveTab] =
    useState("standings");
  const [prevStandings, setPrevStandings] =
    useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] =
    useState("");
  const liveRefreshTimerRef = useRef(null);

  const theme = getTournamentTheme(
    tournament?.theme_key
  );

  const fetchTournament = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }

    try {
      const {
        data: tournamentData,
        error: tournamentError,
      } = await supabase
        .from("tournaments")
        .select("*")
        .eq("id", id)
        .single();

      if (tournamentError) {
        throw tournamentError;
      }

      const {
        data: tournamentPlayers,
        error: playersError,
      } = await supabase
        .from("tournament_players")
        .select("player_id, players (*)")
        .eq("tournament_id", id);

      if (playersError) {
        throw playersError;
      }

      const playerData = (
        tournamentPlayers || []
      )
        .filter((item) => item.players)
        .map((item) => ({
          id: item.players.id,
          name: item.players.name,
          handicap: item.players.handicap,
          club: item.players.home_club,
          image:
            item.players.image_yrl ||
            item.players.image_url ||
            null,
          scores: [],
          miniGameScores: {},
        }));

      const playerIds = playerData.map(
        (player) => player.id
      );

      const {
        data: roundsData,
        error: roundsError,
      } = await supabase
        .from("rounds")
        .select("*")
        .eq("tournament_id", id)
        .order("order_index", {
          ascending: true,
        });

      if (roundsError) {
        throw roundsError;
      }

      const roundIds = (roundsData || []).map(
        (round) => round.id
      );

      let modesData = [];
      let scrambleData = [];
      let customPairsData = [];
      let scoresData = [];

      if (roundIds.length > 0) {
        const [
          modesResponse,
          scrambleResponse,
          customPairsResponse,
        ] = await Promise.all([
          supabase
            .from("round_modes")
            .select("*")
            .in("round_id", roundIds),
          supabase
            .from("scramble_settings")
            .select("*")
            .in("round_id", roundIds),
          supabase
            .from("scramble_custom_pairs")
            .select("*")
            .in("round_id", roundIds),
        ]);

        if (modesResponse.error) {
          throw modesResponse.error;
        }

        if (scrambleResponse.error) {
          throw scrambleResponse.error;
        }

        if (customPairsResponse.error) {
          throw customPairsResponse.error;
        }

        modesData = modesResponse.data || [];
        scrambleData =
          scrambleResponse.data || [];
        customPairsData =
          customPairsResponse.data || [];

        if (playerIds.length > 0) {
          const { data, error } =
            await supabase
              .from("scores")
              .select("*")
              .in("round_id", roundIds)
              .in("player_id", playerIds);

          if (error) {
            throw error;
          }

          scoresData = data || [];
        }
      }

      const {
        data: miniGames,
        error: miniGamesError,
      } = await supabase
        .from("mini_games")
        .select("*")
        .eq("tournament_id", id);

      if (miniGamesError) {
        console.error(
          "Kunde inte hämta sidotävlingar:",
          miniGamesError
        );
      }

      let miniGameScoresData = [];

      if (
        roundIds.length > 0 &&
        playerIds.length > 0
      ) {
        const { data, error } = await supabase
          .from("mini_game_scores")
          .select(
            `
              id,
              value,
              player_id,
              round_id,
              mini_game_id,
              mini_games!inner (
                id,
                name,
                tournament_id
              )
            `
          )
          .eq("mini_games.tournament_id", id)
          .in("round_id", roundIds)
          .in("player_id", playerIds);

        if (error) {
          console.error(
            "Kunde inte hämta resultat för sidotävling:",
            error
          );
        } else {
          miniGameScoresData = data || [];
        }
      }

      const rounds = (roundsData || []).map(
        (round) => {
          const scrambleSetting =
            scrambleData.find(
              (setting) =>
                setting.round_id === round.id
            );

          const customPairs =
            customPairsData.filter(
              (pair) =>
                pair.round_id === round.id
            );

          const customTeams = {};

          customPairs.forEach((pair) => {
            if (
              pair.player_1_id &&
              pair.player_2_id
            ) {
              customTeams[pair.player_1_id] =
                pair.player_2_id;
              customTeams[pair.player_2_id] =
                pair.player_1_id;
            }
          });

          return {
            ...round,
            modes: modesData
              .filter(
                (mode) =>
                  mode.round_id === round.id
              )
              .sort(
                (a, b) =>
                  (a.position ?? 0) -
                  (b.position ?? 0)
              )
              .map((mode) => mode.mode),
            scrambleOptions: scrambleSetting
              ? {
                  pairing:
                    scrambleSetting.pairing,
                  scrambleWithHandicap:
                    scrambleSetting.scramble_with_handicap,
                  lowPct:
                    scrambleSetting.low_hcp,
                  highPct:
                    scrambleSetting.high_hcp,
                  customTeams,
                }
              : null,

            /*
             * Viktigt för live-scorekort och EditScores:
             * använd sparade lag från rounds.teams i stället
             * för att alltid skriva över dem med [].
             */
            teams: Array.isArray(round.teams)
              ? round.teams
              : [],
          };
        }
      );

      playerData.forEach((player) => {
        player.scores = rounds.map((round) => {
          const score = scoresData.find(
            (item) =>
              item.round_id === round.id &&
              item.player_id === player.id
          );

          return score?.score || 0;
        });

        player.miniGameScores = {};

        rounds.forEach((round, roundIndex) => {
          const roundScores =
            miniGameScoresData.filter(
              (score) =>
                score.round_id === round.id &&
                score.player_id === player.id
            );

          if (roundScores.length === 0) {
            return;
          }

          player.miniGameScores[roundIndex] = {};

          roundScores.forEach((score) => {
            const gameName =
              score.mini_games?.name;

            if (gameName) {
              player.miniGameScores[
                roundIndex
              ][gameName] = score.value || 0;
            }
          });
        });
      });

      const finalTournament = {
        ...tournamentData,
        players: playerIds,
        playerData,
        rounds,
        miniGames: miniGames || [],
      };

      setTournament(finalTournament);
      updateStandings(finalTournament);
    } catch (error) {
      console.error(
        "Kunde inte hämta turneringen:",
        error
      );
      setLoadError(
        "Turneringen kunde inte hämtas. Försök att ladda om sidan."
      );
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  useEffect(() => {
    const scheduleSilentRefresh = () => {
      if (liveRefreshTimerRef.current) {
        window.clearTimeout(liveRefreshTimerRef.current);
      }

      liveRefreshTimerRef.current = window.setTimeout(() => {
        fetchTournament(true);
      }, 350);
    };

    const channel = supabase
      .channel(`tournament-live-results-${id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "scores" },
        scheduleSilentRefresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "mini_game_scores" },
        scheduleSilentRefresh
      )
      .subscribe();

    return () => {
      if (liveRefreshTimerRef.current) {
        window.clearTimeout(liveRefreshTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [fetchTournament, id]);

  useEffect(() => {
    if (
      !authLoading &&
      user &&
      tournament
    ) {
      setIsAdmin(
        user.id === tournament.user_id
      );
      return;
    }

    if (!authLoading && !user) {
      setIsAdmin(false);
    }
  }, [user, tournament, authLoading]);

  function updateStandings(data) {
    if (!data?.playerData) {
      return;
    }

    const sorted = [...data.playerData].sort(
      (a, b) => {
        const totalA = a.scores.reduce(
          (sum, value) =>
            sum + (value || 0),
          0
        );
        const totalB = b.scores.reduce(
          (sum, value) =>
            sum + (value || 0),
          0
        );

        return totalA - totalB;
      }
    );

    setPrevStandings(
      sorted.map((player) => player.id)
    );
  }

  if (loading) {
    return (
      <div className={`${theme.page} min-h-screen`}>
        <div className="mx-auto flex min-h-[55vh] max-w-5xl items-center justify-center px-4">
          <div
            className={`${theme.section} ${theme.border} ${theme.textPrimary} flex items-center gap-3 rounded-2xl border px-5 py-4 shadow-sm`}
          >
            <span
              className={`${theme.primarySoft} ${theme.heading} flex h-9 w-9 items-center justify-center rounded-full`}
              aria-hidden="true"
            >
              ⛳
            </span>
            <div>
              <p className="font-semibold">
                Laddar turnering
              </p>
              <p
                className={`${theme.textSecondary} text-xs`}
              >
                Resultat och spelare hämtas…
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !tournament) {
    return (
      <div className={`${theme.page} min-h-screen`}>
        <div className="mx-auto flex min-h-[55vh] max-w-xl items-center justify-center px-4">
          <section
            className={`${theme.section} ${theme.border} rounded-2xl border p-6 text-center shadow-sm`}
          >
            <h1
              className={`${theme.heading} text-xl font-semibold`}
            >
              Turneringen kunde inte öppnas
            </h1>
            <p
              className={`${theme.textSecondary} mt-2 text-sm leading-6`}
            >
              {loadError}
            </p>
            <button
              type="button"
              onClick={fetchTournament}
              className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} mt-5 rounded-xl px-5 py-2.5 text-sm font-semibold`}
            >
              Försök igen
            </button>
          </section>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: "standings",
      label: "Min turnering",
    },
    ...(tournament.miniGames?.length > 0
      ? [
          {
            id: "mini-games",
            label: "Sidotävlingar",
          },
        ]
      : []),
    {
      id: "players",
      label: "Spelare",
    },
  ];

  if (
    isAdmin &&
    !tabs.find(
      (tab) => tab.id === "edit-scores"
    )
  ) {
    tabs.push({
      id: "edit-scores",
      label: "Redigera resultat",
    });
  }

  if (
    isAdmin &&
    !tabs.find(
      (tab) => tab.id === "edit-tournament"
    )
  ) {
    tabs.push({
      id: "edit-tournament",
      label: "Redigera turnering",
    });
  }

  const playerObjects = tournament.players.map(
    (playerId) => {
      const player =
        tournament.playerData.find(
          (item) => item.id === playerId
        );

      return {
        id: player?.id || playerId,
        name: player?.name || "Okänd",
        scores:
          player?.scores ||
          tournament.rounds.map(() => 0),
      };
    }
  );

  return (
    <div
      className={`${theme.page} ${theme.textPrimary} min-h-screen`}
    >
      <header
        className={`${theme.primary} ${theme.textOnPrimary} relative overflow-hidden border-b border-black/10`}
      >
        <div
          className={`${theme.accentBackground} pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full opacity-10 blur-3xl`}
        />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-4 pb-6 pt-6 sm:px-5 sm:pb-8 sm:pt-7 lg:px-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/60 sm:text-[11px]">
                Golfturnering
              </p>

              <h1 className="mt-2 break-words text-2xl font-semibold tracking-tight sm:text-3xl">
                {tournament.name || "Turnering"}
              </h1>

              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                Resultat, rondinformation, spelare,
                digitala scorekort och sidotävlingar
                samlade på ett ställe.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 sm:justify-end">
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium">
                {tournament.playerData?.length ||
                  0}{" "}
                spelare
              </span>

              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium">
                {tournament.rounds?.length || 0}{" "}
                {tournament.rounds?.length === 1
                  ? "runda"
                  : "rundor"}
              </span>

              {tournament.miniGames?.length >
                0 && (
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium">
                  {tournament.miniGames.length} mini{" "}
                  {tournament.miniGames
                    .length === 1
                    ? "game"
                    : "games"}
                </span>
              )}

              {isAdmin && (
                <span
                  className={`${theme.accentSoft} ${theme.accent} rounded-full px-3 py-1.5 text-xs font-semibold`}
                >
                  Administratör
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-3 pb-12 sm:px-5 lg:px-6">
        <nav
          className="relative z-10 -mt-3 sm:-mt-4"
          aria-label="Turneringsmeny"
        >
          <div
            className={`${theme.section} ${theme.border} overflow-x-auto rounded-2xl border p-1.5 shadow-[0_8px_24px_rgba(31,45,38,0.10)]`}
          >
            <div
              className="flex min-w-max items-center gap-1"
              role="tablist"
            >
              {tabs.map((tab) => {
                const isActive =
                  activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() =>
                      setActiveTab(tab.id)
                    }
                    className={`whitespace-nowrap rounded-xl px-3.5 py-2.5 text-xs font-semibold transition sm:px-4 sm:text-sm ${
                      isActive
                        ? `${theme.primary} ${theme.textOnPrimary} shadow-sm`
                        : `${theme.textSecondary} hover:bg-black/[0.035] hover:text-[#1f2d26]`
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        {activeTab === "standings" &&
          isAdmin && (
            <LiveRoundControl
              tournament={tournament}
              theme={theme}
              isAdmin={isAdmin}
            />
          )}

        <div className="mt-4 sm:mt-5">
          {activeTab === "standings" && (
            <TournamentStandings
              players={playerObjects}
              rounds={tournament.rounds.map(
                (round) => round.name
              )}
              tournamentModes={tournament.rounds.map(
                (round) => round.modes?.[0]
              )}
              tournament={tournament}
              setTournament={setTournament}
              prevStandings={prevStandings}
            />
          )}

          {activeTab === "mini-games" && (
            <MiniGamesStandings
              tournament={tournament}
            />
          )}

          {activeTab === "players" && (
            <TournamentPlayers
              tournament={tournament}
              updateTournament={setTournament}
              isAdmin={isAdmin}
              setActiveTab={setActiveTab}
            />
          )}

          {activeTab === "edit-scores" && (
            <EditScores
              tournament={tournament}
              setPrevStandings={setPrevStandings}
              setTournament={setTournament}
              setActiveTab={setActiveTab}
              isAdmin={isAdmin}
            />
          )}

          {activeTab === "edit-tournament" &&
            tournament && (
              <NewTournament
                editMode
                tournamentData={tournament}
                setActiveTab={setActiveTab}
                setTournament={setTournament}
                onSaved={() => {
                  fetchTournament();
                }}
              />
            )}
        </div>
      </main>
    </div>
  );
}